import { useState } from "react";
import { toast } from "sonner";
import { Briefcase } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "./ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "./ui/select";
import { Checkbox } from "./ui/checkbox";
import { MailDraftDialog } from "./MailDraftDialog";
import { ConfirmDelete } from "./ConfirmDelete";
import { api, apiError } from "../lib/api";

const KARR_STATUS = [
  { value: "eingegangen", label: "Eingegangen" },
  { value: "kontaktiert", label: "Kontaktiert" },
  { value: "gespraech", label: "Im Gespräch" },
  { value: "gestartet", label: "Gestartet (neben-/hauptberuflich)" },
  { value: "kein_interesse", label: "Kein Interesse" },
];

export function KarriereDialog() {
  const [data, setData] = useState({ interessenten: [], empfehlungen: [] });
  const load = async () => {
    try {
      const { data } = await api.get("/berater/karriere");
      setData(data);
    } catch {
      /* ignore */
    }
  };
  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/karriere/${id}/status`, { status });
      toast.success(status === "gestartet" ? "Gestartet – Kunde wurde informiert." : "Status aktualisiert.");
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || err.message);
    }
  };
  const confirmConsent = async (id) => {
    try {
      await api.post(`/karriere/${id}/zustimmung`);
      toast.success("Zustimmung erfasst – Kontaktdaten sind nun sichtbar.");
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || err.message);
    }
  };
  const deleteEmpfehlung = async (id) => {
    try {
      await api.delete(`/karriere/${id}`);
      toast.success("Fall gelöscht.");
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || err.message);
    }
  };
  const removeInteressent = async (email) => {
    try {
      await api.post(`/berater/interessent/entfernen`, { email });
      toast.success("Interessent entfernt.");
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || err.message);
    }
  };
  const [selectMode, setSelectMode] = useState(false);
  const [selEmpf, setSelEmpf] = useState(() => new Set());
  const [selInt, setSelInt] = useState(() => new Set());
  const toggleEmpf = (id) => setSelEmpf((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const toggleInt = (email) => setSelInt((s) => { const n = new Set(s); if (n.has(email)) n.delete(email); else n.add(email); return n; });
  const totalSel = selEmpf.size + selInt.size;
  const exitSelect = () => { setSelectMode(false); setSelEmpf(new Set()); setSelInt(new Set()); };
  const doBulk = async () => {
    try {
      if (selEmpf.size) await api.post(`/karriere/bulk-delete`, { ids: [...selEmpf] });
      if (selInt.size) await api.post(`/berater/interessent/bulk-entfernen`, { emails: [...selInt] });
      toast.success(`${totalSel} Einträge gelöscht.`);
      exitSelect();
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || err.message);
    }
  };
  const hasItems = (data.interessenten?.length || 0) + (data.empfehlungen?.length || 0) > 0;
  return (
    <Dialog onOpenChange={(o) => o && load()}>
      <DialogTrigger asChild>
        <button data-testid="karriere-btn" className="flex h-12 w-full items-center justify-center gap-2 rounded-full border border-[#0A1128]/15 bg-white text-sm font-semibold text-[#0A1128] transition-colors hover:bg-[#F1F5F9]">
          <Briefcase size={18} weight="fill" color="#C5A059" /> Karriere-Postfach
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-md rounded-2xl" data-testid="karriere-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-light tracking-tight">Karriere-Postfach</DialogTitle>
          <DialogDescription>Interessenten & empfohlene Kandidaten.</DialogDescription>
        </DialogHeader>
        {hasItems && (
          <div className="flex items-center justify-between rounded-xl border border-[#E2E8F0] bg-white px-3 py-2" data-testid="karriere-bulk-bar">
            {selectMode ? (
              <>
                <button data-testid="karriere-select-cancel" onClick={exitSelect} className="text-sm font-semibold text-[#718096]">Fertig</button>
                <span className="text-xs text-[#718096]" data-testid="karriere-selected-count">{totalSel} ausgewählt</span>
                {totalSel > 0 ? (
                  <ConfirmDelete name={`${totalSel} Einträge`} testid="karriere-bulk-delete-btn" label={`${totalSel} löschen`} onConfirm={doBulk} />
                ) : (
                  <span className="text-xs font-semibold text-[#CBD5E0]" data-testid="karriere-bulk-delete-disabled">Löschen</span>
                )}
              </>
            ) : (
              <button data-testid="karriere-select-mode" onClick={() => setSelectMode(true)} className="ml-auto text-sm font-semibold text-[#C5A059]">Mehrere löschen</button>
            )}
          </div>
        )}
        <div className="max-h-[60vh] space-y-5 overflow-y-auto pt-1">
          <section>
            <h3 className="mb-2 text-[11px] font-bold uppercase tracking-[0.12em] text-[#C5A059]">Eigen-Interesse ({data.interessenten?.length || 0})</h3>
            {(!data.interessenten || data.interessenten.length === 0) && <p className="text-sm text-[#A0AEC0]">Noch keine.</p>}
            <div className="space-y-2">
              {data.interessenten?.map((i, idx) => (
                <div key={idx} className={`rounded-lg border p-3 ${selectMode && selInt.has(i.email) ? "border-[#C5A059] ring-1 ring-[#C5A059]" : "border-[#E2E8F0]"}`} data-testid="karriere-interessent">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex min-w-0 flex-1 items-center gap-2">
                      {selectMode && <Checkbox data-testid="karriere-int-checkbox" checked={selInt.has(i.email)} onCheckedChange={() => toggleInt(i.email)} className="h-5 w-5 border-[#C5A059] data-[state=checked]:border-[#C5A059] data-[state=checked]:bg-[#C5A059]" />}
                      <p className="min-w-0 flex-1 truncate text-sm font-semibold text-[#0A1128]">{i.email}</p>
                    </div>
                    {!selectMode && <ConfirmDelete name={i.email} testid="interessent-delete-btn" label="" className="shrink-0 text-[#C0392B] hover:text-[#a93226]" onConfirm={() => removeInteressent(i.email)} />}
                  </div>
                  {!selectMode && <MailDraftDialog typ="interessent" email={i.email} empfaengerName={i.email} />}
                </div>
              ))}
            </div>
          </section>
          <section>
            <h3 className="mb-2 text-[11px] font-bold uppercase tracking-[0.12em] text-[#C5A059]">Empfohlene Kandidaten ({data.empfehlungen?.length || 0})</h3>
            {(!data.empfehlungen || data.empfehlungen.length === 0) && <p className="text-sm text-[#A0AEC0]">Noch keine.</p>}
            <div className="space-y-2">
              {data.empfehlungen?.map((e) => (
                <div key={e.id} className={`rounded-lg border p-3 ${selectMode && selEmpf.has(e.id) ? "border-[#C5A059] ring-1 ring-[#C5A059]" : "border-[#E2E8F0]"}`} data-testid="karriere-empfehlung">
                  {selectMode && (
                    <label className="mb-2 flex items-center gap-2">
                      <Checkbox data-testid="karriere-empf-checkbox" checked={selEmpf.has(e.id)} onCheckedChange={() => toggleEmpf(e.id)} className="h-5 w-5 border-[#C5A059] data-[state=checked]:border-[#C5A059] data-[state=checked]:bg-[#C5A059]" />
                      <span className="text-xs font-medium text-[#718096]">Auswählen</span>
                    </label>
                  )}
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-[#0A1128]">{e.name}</p>
                      {e.bestaetigt ? (
                        <p className="text-xs text-[#4A5568]">{e.kontakt}</p>
                      ) : e.kontakt_typ === "telefon" ? (
                        <p className="text-xs italic text-[#DD6B20]">Zustimmung telefonisch einholen</p>
                      ) : (
                        <p className="text-xs italic text-[#DD6B20]">Wartet auf Online-Bestätigung</p>
                      )}
                    </div>
                    {e.belohnt && <span className="shrink-0 rounded-full bg-[#C5A059]/15 px-2 py-0.5 text-[10px] font-bold text-[#8a6d2f]" data-testid="karriere-praemie-badge">Prämie ✓</span>}
                  </div>
                  {e.notiz && <p className="mt-1 text-xs italic text-[#718096]">„{e.notiz}“</p>}
                  <p className="mt-1 text-[11px] text-[#A0AEC0]">von {e.kunde_email}</p>
                  {!selectMode && (
                    <>
                      {!e.bestaetigt && e.kontakt_typ === "telefon" && (
                        <button data-testid="karriere-zustimmung-btn" onClick={() => confirmConsent(e.id)} className="mt-2 h-9 w-full rounded-lg border border-[#276749]/30 bg-[#276749]/5 text-xs font-semibold text-[#276749] active:scale-[0.99]">
                          Telefonische Zustimmung bestätigen
                        </button>
                      )}
                      <Select value={e.status || "eingegangen"} onValueChange={(v) => updateStatus(e.id, v)}>
                        <SelectTrigger data-testid="karriere-status-select" className="mt-2 h-10 rounded-lg border-[#E2E8F0] bg-[#F8F9FA] text-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {KARR_STATUS.map((o) => (
                            <SelectItem key={o.value} value={o.value} data-testid={`karriere-status-${o.value}`}>{o.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {e.status === "gestartet" && <p className="mt-1.5 text-[11px] font-medium text-[#276749]">Kunde wurde über die erfolgreiche Empfehlung informiert.</p>}
                      {e.bestaetigt && <MailDraftDialog typ="karriere" id={e.id} empfaengerName={e.name} />}
                      <div className="mt-2 flex justify-end border-t border-[#F1F5F9] pt-2">
                        <ConfirmDelete name={e.name} testid="karriere-delete-btn" label="Fall löschen" onConfirm={() => deleteEmpfehlung(e.id)} />
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          </section>
        </div>
      </DialogContent>
    </Dialog>
  );
}
